import { useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ProgramData } from '@/pages/add-program';

interface Step2DescriptionProps {
  data: ProgramData;
  updateData: (updates: Partial<ProgramData>) => void;
  onValidationChange: (isValid: boolean) => void;
}

export default function Step2Description({ data, updateData, onValidationChange }: Step2DescriptionProps) {
  // Validation
  useEffect(() => {
    const isValid = !!data.name && !!data.notesGeneral;
    onValidationChange(isValid);
  }, [data.name, data.notesGeneral, onValidationChange]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateData({ name: e.target.value });
  };

  const handleSubtitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateData({ subtitle: e.target.value });
  };

  const handleNotesGeneralChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    updateData({ notesGeneral: e.target.value });
  };

  const handleNotesWhoChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    updateData({ notesWho: e.target.value });
  };

  const handleNotesAdviceChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    updateData({ notesAdvice: e.target.value });
  };

  return (
    <div className="space-y-6">
      {/* Program Name */}
      <div className="space-y-2">
        <Label htmlFor="program-name" className="text-sm font-medium text-slate-700">
          Program Name <span className="text-red-500">*</span>
        </Label>
        <Input
          id="program-name"
          data-testid="input-program-name"
          value={data.name}
          onChange={handleNameChange}
          placeholder="Enter program name"
          className="w-full"
        />
      </div>

      {/* Subtitle */}
      <div className="space-y-2">
        <Label htmlFor="program-subtitle" className="text-sm font-medium text-slate-700">
          Subtitle
        </Label>
        <Input
          id="program-subtitle"
          data-testid="input-program-subtitle"
          value={data.subtitle}
          onChange={handleSubtitleChange}
          placeholder="Enter program subtitle (optional)"
          className="w-full"
        />
      </div>

      {/* What is it? */}
      <div className="space-y-2">
        <Label htmlFor="notes-general" className="text-sm font-medium text-slate-700">
          What is it? <span className="text-red-500">*</span>
        </Label>
        <Textarea
          id="notes-general"
          data-testid="textarea-notes-general"
          value={data.notesGeneral}
          onChange={handleNotesGeneralChange}
          placeholder="Describe what this program is about..."
          className="w-full h-32 resize-none"
        />
      </div>

      {/* Who is it for? */}
      <div className="space-y-2">
        <Label htmlFor="notes-who" className="text-sm font-medium text-slate-700">
          Who is it for?
        </Label>
        <Textarea
          id="notes-who"
          data-testid="textarea-notes-who"
          value={data.notesWho}
          onChange={handleNotesWhoChange}
          placeholder="Describe who this program is designed for..."
          className="w-full h-32 resize-none"
        />
      </div>

      {/* How should you do it? */}
      <div className="space-y-2">
        <Label htmlFor="notes-advice" className="text-sm font-medium text-slate-700">
          How should you do it?
        </Label>
        <Textarea
          id="notes-advice"
          data-testid="textarea-notes-advice"
          value={data.notesAdvice}
          onChange={handleNotesAdviceChange}
          placeholder="Provide advice on how to approach this program..."
          className="w-full h-32 resize-none"
        />
      </div>

      {/* Character counts for guidance */}
      <div className="text-xs text-slate-500 space-y-1">
        <p>Name: {data.name.length} characters</p>
        <p>Description: {data.notesGeneral.length} characters</p>
        <p>Target audience: {data.notesWho.length} characters</p>
        <p>Advice: {data.notesAdvice.length} characters</p>
      </div>
    </div>
  );
}

